import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "ol/ol.css";
import StarIcon from "@mui/icons-material/Star";
import styles from "../Stores/Stores.module.scss";
import mapStyles from "../Mapa/Mapa.module.scss";
import { getLocalById } from "../../services/api.tsx";
import Estabelecimento from "../../domain/Estabelecimentos.tsx";
import { Feature, Map, View } from "ol";
import TileLayer from "ol/layer/Tile.js";
import { OSM } from "ol/source.js";
import { fromLonLat, toLonLat } from "ol/proj.js";
import Point from "ol/geom/Point.js";
import LineString from "ol/geom/LineString.js";
import VectorSource from "ol/source/Vector.js";
import VectorLayer from "ol/layer/Vector.js";
import Geolocation from "ol/Geolocation.js";
import Style from "ol/style/Style.js";
import Icon from "ol/style/Icon.js";
import { Circle as CircleStyle, Fill, Stroke } from "ol/style.js";

const StoreDetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState<Estabelecimento | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const mapElement = useRef<HTMLDivElement | null>(null);
  const [detailMap, setDetailMap] = useState<Map | null>(null);
  const userLocationSourceRef = useRef(new VectorSource());
  const storeSourceRef = useRef(new VectorSource());
  const routeSourceRef = useRef(new VectorSource());
  const routeFeatureRef = useRef<Feature<LineString> | null>(null);
  const userFeatureRef = useRef<Feature<Point> | null>(null);
  const [userLocationAvailable, setUserLocationAvailable] = useState(false);
  // mapError removed
  const [routeInfo, setRouteInfo] = useState<{ distance: number; duration: number } | null>(null);

  useEffect(() => {
    const storeId = Number(id);
    if (!storeId) {
      setLoading(false);
      return;
    }

    getLocalById(storeId)
      .then((local) => {
        if (local) {
          setStore(local);
        }
      })
      .catch((error) => {
        console.error("Erro ao carregar estabelecimento:", error);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const rawStore = store as any;
  const normalizePhoto = (photo: any): string | undefined => {
    if (!photo) return undefined;
    if (typeof photo === "string") {
      const trimmed = photo.trim();
      if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("/")) {
        return trimmed;
      }
      return `/${trimmed.replace(/^\/+/, "")}`;
    }
    const url = photo?.url || photo?.path || photo?.src || photo?.imagem;
    if (!url) return undefined;
    return normalizePhoto(url);
  };

  const gallery = Array.isArray(rawStore?.fotos) && rawStore?.fotos.length
    ? rawStore.fotos.map(normalizePhoto).filter(Boolean) as string[]
    : typeof rawStore?.imagem === "string"
    ? [normalizePhoto(rawStore.imagem)!]
    : ["https://via.placeholder.com/900x500?text=Sem+imagem"];

  const tags = Array.isArray(rawStore?.categoria)
    ? rawStore.categoria.map((categoria: any) =>
        typeof categoria === "string" ? categoria : categoria?.nomeCategoria || categoria?.nome,
      ).filter(Boolean)
    : typeof rawStore?.categoria === "string"
    ? [rawStore.categoria]
    : [];

  const parseCoord = (value: any): number | null => {
    if (value === undefined || value === null) return null;
    if (typeof value === "number") return value;
    const normalized = String(value).trim().replace(",", ".");
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const lat = parseCoord(rawStore?.latitude);
  const lng = parseCoord(rawStore?.longitude);
  const hasCoordinates = lat !== null && lng !== null;
  const storePosition = hasCoordinates ? fromLonLat([lng as number, lat as number]) : null;

  // no-op: removed debug logging

  useEffect(() => {
    if (!store || detailMap || !hasCoordinates || !mapElement.current || !storePosition) return;
    try {

      const view = new View({
        center: storePosition,
        zoom: 15,
      });

      const map = new Map({
        target: mapElement.current as HTMLElement,
        layers: [
          new TileLayer({ source: new OSM() }),
          new VectorLayer({ source: routeSourceRef.current }),
          new VectorLayer({ source: storeSourceRef.current }),
          new VectorLayer({ source: userLocationSourceRef.current }),
        ],
        view,
        controls: [],
      });


      const storeFeature = new Feature({ geometry: new Point(storePosition) });
      storeFeature.setStyle(
        new Style({
          image: new Icon({ anchor: [0.5, 1], scale: 0.08, src: "https://cdn-icons-png.flaticon.com/512/684/684908.png" }),
        }),
      );
      storeSourceRef.current.addFeature(storeFeature);

      const routeFeature = new Feature({ geometry: new LineString([]) });
      routeFeature.setStyle(
        new Style({ stroke: new Stroke({ color: "#2563eb", width: 4, lineCap: "round" }) }),
      );
      routeSourceRef.current.addFeature(routeFeature);
      routeFeatureRef.current = routeFeature;

      const userFeature = new Feature();
      userFeature.setStyle(
        new Style({ image: new CircleStyle({ radius: 8, fill: new Fill({ color: "#1d4ed8" }), stroke: new Stroke({ color: "#fff", width: 3 }) }) }),
      );
      userFeatureRef.current = userFeature;
      userLocationSourceRef.current.addFeature(userFeature);

      const geolocation = new Geolocation({ trackingOptions: { enableHighAccuracy: true }, projection: view.getProjection() });
      geolocation.on("change:position", () => {
        const position = geolocation.getPosition();
        if (!position) return;
        userFeature.setGeometry(new Point(position));
        setUserLocationAvailable(true);

        // Convert OL map position (map projection) to lon/lat for routing
        const [userLon, userLat] = toLonLat(position);
        const storeLon = lng as number;
        const storeLat = lat as number;

        // Request route from OSRM public server
        const url = `https://router.project-osrm.org/route/v1/driving/${userLon},${userLat};${storeLon},${storeLat}?overview=full&geometries=geojson&alternatives=false&steps=false`;
        fetch(url)
          .then((res) => res.json())
          .then((data) => {
            if (!data || !data.routes || !data.routes.length) return;
            const route = data.routes[0];
            const coords: number[][] = route.geometry.coordinates; // [lon, lat]
            const mapped = coords.map(([lon, lat]) => fromLonLat([lon, lat]));
            const olRoute = new LineString(mapped as any);
            routeFeatureRef.current?.setGeometry(olRoute);
            const routeExtent = olRoute.getExtent();
            if (routeExtent) {
              map.getView().fit(routeExtent, { padding: [70, 70, 70, 70], maxZoom: 16, duration: 500 });
            }
            setRouteInfo({ distance: route.distance, duration: route.duration });
          })
          .catch(() => {
            // silent fail — keep straight line fallback
            const route = new LineString([position, storePosition]);
            routeFeatureRef.current?.setGeometry(route);
            const routeExtent = route.getExtent();
            if (routeExtent) {
              map.getView().fit(routeExtent, { padding: [70, 70, 70, 70], maxZoom: 16, duration: 500 });
            }
          });
      });

      geolocation.setTracking(true);
      setDetailMap(map);

      setTimeout(() => {
        try {
          map.updateSize();
          if ((map as any).render) (map as any).render();
        } catch (e) {
          // ignore
        }
      }, 150);

    } catch (err: any) {
      // ignore initialization errors silently
    }

    return () => {
      if (detailMap) detailMap.setTarget(undefined);
    };
  }, [store, detailMap, hasCoordinates, storePosition, lat, lng]);

  useEffect(() => {
    if (detailMap) {
      setTimeout(() => detailMap.updateSize(), 100);
    }
  }, [detailMap]);

  const centerOnRoute = () => {
    if (!detailMap || !routeFeatureRef.current) return;
    const geometry = routeFeatureRef.current.getGeometry();
    if (!geometry) return;
    detailMap.getView().fit(geometry.getExtent(), {
      padding: [70, 70, 70, 70],
      maxZoom: 16,
      duration: 500,
    });
  };

  const city = store?.endereco?.split(",")[1]?.trim() || "Sergipe";
  const activeGalleryImage = gallery[activeImg] || gallery[0];

  if (loading) {
    return <div className={styles.detailPage}>Carregando...</div>;
  }

  if (!store) {
    return (
      <div className={styles.detailPage}>
        <div className={styles.detailHero}>
          <button
            className={styles.detailBackBtn}
            onClick={() => navigate("/estabelecimentos")}
          >
            ← Voltar
          </button>
          <div className={styles.detailHeroContent}>
            <h1>Estabelecimento não encontrado</h1>
            <p>Verifique se o link está correto e tente novamente.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.detailPage}>
      <div className={styles.detailHero}>
        <img
          src={activeGalleryImage}
          alt={store.nome}
          className={styles.detailHeroImg}
        />
        <div className={styles.detailHeroOverlay} />

        <button
          className={styles.detailBackBtn}
          onClick={() => navigate("/estabelecimentos")}
        >
          ← Voltar
        </button>

        <div className={styles.detailHeroContent}>
          <div className={styles.detailTags}>
            {tags.map((t) => (
              <span key={t} className={styles.detailTag}>
                {t}
              </span>
            ))}
          </div>
          <h1 className={styles.detailTitle}>{store.nome}</h1>
          <p className={styles.detailSubtitle}>
            {city} · {store.descricao}
          </p>
        </div>

        <div className={styles.detailRatingBadge}>
          <span className={styles.detailRatingNumber}>
            {store.avaliacao?.toFixed(1) ?? "0.0"}
          </span>
          <div className={styles.detailStars}>
            {[1, 2, 3, 4, 5].map((s) => (
              <StarIcon
                key={s}
                sx={{ fontSize: 13 }}
                className={
                  s <= Math.round(store.avaliacao ?? 0)
                    ? styles.starActive
                    : styles.starInactive
                }
              />
            ))}
          </div>
          <span className={styles.detailReviewCount}>{store.avaliacao ?? 0} avaliações</span>
        </div>

        {gallery.length > 1 && (
          <div className={styles.detailThumbs}>
            {gallery.map((img, i) => (
              <button
                key={i}
                className={`${styles.detailThumb} ${i === activeImg ? styles.detailThumbActive : ""}`}
                onClick={() => setActiveImg(i)}
              >
                <img src={img} alt="" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className={styles.detailBody}>
        <div className={styles.detailLeft}>
          <div className={styles.detailSection}>
            <h2 className={styles.detailSectionTitle}>Sobre o estabelecimento</h2>
            <p className={styles.detailSobre}>{store.descricao}</p>
          </div>

          <div className={styles.detailSection}>
            <h2 className={styles.detailSectionTitle}>Informações</h2>
            <div className={styles.infoCardBody}>
              {store.endereco && (
                <div className={styles.infoRow}>
                  <span className={styles.infoIcon}>📍</span>
                  <div>
                    <div className={styles.infoLabel}>Endereço</div>
                    <div className={styles.infoValue}>{store.endereco}</div>
                  </div>
                </div>
              )}
              {store.telefone && (
                <div className={styles.infoRow}>
                  <span className={styles.infoIcon}>📞</span>
                  <div>
                    <div className={styles.infoLabel}>Telefone</div>
                    <div className={styles.infoValue}>{store.telefone}</div>
                  </div>
                </div>
              )}
              {store.horarioFuncionamento && (
                <div className={styles.infoRow}>
                  <span className={styles.infoIcon}>🕐</span>
                  <div>
                    <div className={styles.infoLabel}>Horário</div>
                    <div className={styles.infoValue}>{store.horarioFuncionamento}</div>
                  </div>
                </div>
              )}
              {store.mediaPreco && (
                <div className={styles.infoRow}>
                  <span className={styles.infoIcon}>💰</span>
                  <div>
                    <div className={styles.infoLabel}>Faixa de preço</div>
                    <div className={styles.infoValue}>{store.mediaPreco}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.detailSection}>
            <h2 className={styles.detailSectionTitle}>Localização</h2>
            <div className={styles.mapPreview}>
              {hasCoordinates ? (
                <div className={mapStyles.mapWrapper}>
                  <div className={mapStyles.mapArea}>
                    <div
                      ref={(el) => {
                        mapElement.current = el;
                      }}
                      className={mapStyles.mapCanvas}
                    />
                  </div>
                </div>
              ) : (
                <div className={styles.noMapMessage}>
                  Localização não disponível para este estabelecimento.
                </div>
              )}
              {/* debug removed */}
            </div>
          </div>
        </div>

        <div className={styles.detailRight}>
          <div className={styles.infoCard}>
            <div className={styles.infoCardHeader}>Ações</div>
            <div className={styles.infoCardBody}>
              <button
                className={styles.infoCtaPrimary}
                onClick={() => window.open(`https://www.google.com/maps/search/${encodeURIComponent(store.endereco || store.nome)}`, "_blank")}
              >
                🗺️ Abrir no mapa
              </button>
              {userLocationAvailable && (
                <button
                  className={styles.infoCtaSecondary}
                  onClick={centerOnRoute}
                >
                  🧭 Mostrar rota até o local
                </button>
              )}
              {store.telefone && (
                <button
                  className={styles.infoCtaSecondary}
                  onClick={() => window.open(`tel:${store.telefone.replace(/[^0-9+]/g, "")}`)}
                >
                  📞 Ligar
                </button>
              )}
              {routeInfo && (
                <div style={{ marginTop: 12 }}>
                  <div className={styles.infoLabel}>Rota</div>
                  <div className={styles.infoValue}>
                    {`${(routeInfo.distance / 1000).toFixed(2)} km • ${Math.ceil(routeInfo.duration / 60)} min`}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreDetails;
